/** Tiny WebAudio cues. Muted by default; the AudioContext is created only after the player enables sound. */
let ctx: AudioContext | null = null;
let volume = 1;

/** 0..1 master volume (embed command or settings). */
export function setVolume(v: number): void {
  volume = Math.min(1, Math.max(0, v));
}

export function enableAudio(): void {
  try {
    ctx ??= new AudioContext();
    void ctx.resume();
  } catch {
    ctx = null;
  }
}

/** One synthesized note: frequency glide, attack and exponential release (no audio files to license). */
function note(freq: number, ms: number, o: { gain?: number; to?: number; at?: number; type?: OscillatorType } = {}): void {
  if (!ctx || volume === 0) return;
  const t0 = ctx.currentTime + (o.at ?? 0) / 1000;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + ms / 1000);
  const peak = (o.gain ?? 0.05) * volume;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + ms / 1000);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + ms / 1000 + 0.02);
}

function tone(freq: number, ms: number, gain = 0.04): void {
  note(freq, ms, { gain });
}

let lastPlus = 0;

export const sounds = {
  count: () => tone(880, 90),
  result: () => tone(523, 260, 0.05),
  /** A counted vehicle: a short bright "pip" (at most ~12 a second so a burst does not buzz). */
  plus: (n = 0) => {
    const now = performance.now();
    if (now - lastPlus < 80) return;
    lastPlus = now;
    note(n % 2 ? 1320 : 1180, 70, { gain: 0.035, type: 'triangle' });
  },
  /** Countdown to the counted minute: soft ticks, the last three louder and higher. */
  countdown: (secondsLeft: number) => (secondsLeft <= 3 ? note(990, 130, { gain: 0.07, type: 'square' }) : note(660, 70, { gain: 0.035, type: 'square' })),
  /** The counted minute starts: a rising sweep. */
  roundStart: () => {
    note(520, 260, { gain: 0.06, to: 1040, type: 'sawtooth' });
    note(1040, 220, { gain: 0.05, at: 230, type: 'triangle' });
  },
  /** The counted minute ends: three falling notes. */
  roundEnd: () => {
    note(784, 160, { gain: 0.05, type: 'triangle' });
    note(659, 160, { gain: 0.05, at: 140, type: 'triangle' });
    note(523, 320, { gain: 0.06, at: 280, type: 'triangle' });
  },
  /** The player won: a short rising arpeggio. */
  win: () => {
    [523, 659, 784, 1047].forEach((f, i) => note(f, 180, { gain: 0.055, at: i * 90, type: 'triangle' }));
  },
};
